import { usePrevious, useSyncedRef } from "@react-hookz/web";
import YouTube from "@vip30/react-youtube";
import { useAtom, useAtomValue } from "jotai";
import { useEffect } from "react";

import { onlyOnePlayingPlayerAtom } from "./atoms";

export const useOnlyOnePlayingPlayer = () => {
  const [{ adsManager, textToSpeechPlayer, youtubePlayer }, setOnlyOnePlayingPlayerAtom] =
    useAtom(onlyOnePlayingPlayerAtom);

  const latestAdsManager = useSyncedRef(adsManager);
  const latestTextToSpeechPlayer = useSyncedRef(textToSpeechPlayer);
  const latestYoutubePlayer = useSyncedRef(youtubePlayer);

  useEffect(() => {
    if (!adsManager) {
      return;
    }

    if (adsManager.getRemainingTime() > 0) {
      latestTextToSpeechPlayer.current?.pause();
      void latestYoutubePlayer.current?.pauseVideo();
    }

    const handleAdStarted = () => {
      latestTextToSpeechPlayer.current?.pause();
      void latestYoutubePlayer.current?.pauseVideo();
      setOnlyOnePlayingPlayerAtom(state => ({ ...state, stopYoutubeAutoplay: false }));
    };
    adsManager.addEventListener(google.ima.AdEvent.Type.STARTED, handleAdStarted);
    adsManager.addEventListener(google.ima.AdEvent.Type.RESUMED, handleAdStarted);
    return () => {
      adsManager.removeEventListener(google.ima.AdEvent.Type.STARTED, handleAdStarted);
      adsManager.removeEventListener(google.ima.AdEvent.Type.RESUMED, handleAdStarted);
    };
  }, [adsManager, latestTextToSpeechPlayer, latestYoutubePlayer, setOnlyOnePlayingPlayerAtom]);

  useEffect(() => {
    if (!textToSpeechPlayer) {
      return;
    }

    const handleStopOtherPlayers = () => {
      latestAdsManager.current?.pause();
      void latestYoutubePlayer.current?.pauseVideo();
      setOnlyOnePlayingPlayerAtom(state => ({ ...state, stopYoutubeAutoplay: true }));
    };

    const handleYoutubeAutoPlayEnabled = () => {
      setOnlyOnePlayingPlayerAtom(state => ({ ...state, stopYoutubeAutoplay: false }));
    };

    textToSpeechPlayer.addEventListener("pause", handleYoutubeAutoPlayEnabled);
    textToSpeechPlayer.addEventListener("play", handleStopOtherPlayers);

    return () => {
      textToSpeechPlayer.removeEventListener("pause", handleYoutubeAutoPlayEnabled);
      textToSpeechPlayer.removeEventListener("play", handleStopOtherPlayers);
    };
  }, [latestAdsManager, latestYoutubePlayer, setOnlyOnePlayingPlayerAtom, textToSpeechPlayer]);

  useEffect(() => {
    if (!youtubePlayer) {
      return;
    }

    // We don't want to pause the ima player when the youtube is playing because of the
    // possibility of the youtube player is playing in the background which is not expected by us
    const pauseOtherPlayerExceptAds = (playerState: number) => {
      if (
        playerState !== YouTube.PlayerState.PLAYING ||
        (latestAdsManager.current && latestAdsManager.current?.getRemainingTime() > 0)
      )
        return;
      latestTextToSpeechPlayer.current?.pause();
    };

    const run = async () => {
      const playerState: number = await youtubePlayer.getPlayerState();
      pauseOtherPlayerExceptAds(playerState);
    };
    void run();

    const handleOnStateChange = (event: CustomEvent) => {
      const { data } = event as unknown as { data: number };
      if (data !== YouTube.PlayerState.PLAYING) return;
      pauseOtherPlayerExceptAds(data);
      setOnlyOnePlayingPlayerAtom(state => ({ ...state, stopYoutubeAutoplay: false }));
    };
    void youtubePlayer.addEventListener("onStateChange", handleOnStateChange);
    return () => {
      void youtubePlayer.removeEventListener("onStateChange", handleOnStateChange);
    };
  }, [latestAdsManager, latestTextToSpeechPlayer, setOnlyOnePlayingPlayerAtom, youtubePlayer]);
  useOnlyOneYoutubePlayerEffect();
};

const useOnlyOneYoutubePlayerEffect = () => {
  const { youtubePlayer } = useAtomValue(onlyOnePlayingPlayerAtom);

  const previousYoutubePlayer = usePrevious(youtubePlayer);

  useEffect(() => {
    const run = async () => {
      const previousVideoIframe = await previousYoutubePlayer?.getIframe();
      const currentVideoIframe = await youtubePlayer?.getIframe();
      if (!previousVideoIframe || !currentVideoIframe) return;
      else if (previousVideoIframe !== currentVideoIframe) {
        void previousYoutubePlayer?.pauseVideo();
      }
    };
    void run();
  }, [previousYoutubePlayer, youtubePlayer]);
};
